import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import jwtDecode from "jwt-decode";
import { authSubject } from "./AuthKeycloak";

interface PermissionsProviderProps {
  children: ReactNode;
}

interface PermissionsContextType {
  hasPermission: (role: string) => boolean;
  loading: boolean;
}

interface JWTToken {
  groups: string[];
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined
);

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({
  children,
}) => {
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const subscription = authSubject.subscribe((auth) => {
      const fetchRoles = async () => {
        try {
          const token = auth?.keycloak?.token;
          if (token) {
            const decodedToken = jwtDecode<JWTToken>(token);
            const userRoles = decodedToken.groups;
            setRoles(userRoles);
          }
        } catch (error) {
          console.error("Error decoding JWT:", error);
        } finally {
          setLoading(false);
        }
      };
 
      if (auth?.keycloak) {
        fetchRoles();
      }
    });
 
    return () => subscription.unsubscribe();
  }, []);

  const hasPermission = (roleOrRoles: string | string[]) => {
    if (Array.isArray(roleOrRoles)) {
      return roleOrRoles.some(role => roles.includes(role));
    }
    return roles.includes(roleOrRoles);
  };

  return (
    <PermissionsContext.Provider value={{ hasPermission, loading }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error(
      "usePermissions deve ser usado dentro de um PermissionsProvider"
    );
  }
  return context;
};
